/**
 * News Ingest Module (AI Advisory P1)
 *
 * Pulls held-instrument news from two proxies and normalizes into ONE lean
 * ephemeral feed for the impact pipeline (P2):
 *   - GAS proxy `?mode=news` — TR: Mynet KAP (per held BIST ticker) + TR RSS macro
 *   - /api/market-intel?topic=news — US/crypto: Yahoo RSS per held ticker
 *
 * Design rules (locked in build plan §6 + no-bloat precondition):
 *   - EPHEMERAL: nothing here is persisted to S/Supabase/localStorage.
 *     Only downstream recommendations (P3) persist, capped, in S.aiCopilot.
 *   - Held pre-filter: only tickers you actually hold (holdings + open trades).
 *   - Graceful degrade: each source can fail independently; partial feed is
 *     a valid feed; empty feed is a valid answer ("no signal").
 *
 * Exports:
 *   fetchAdvisoryNews(opts)  — fetch + normalize + cache (15 min TTL)
 *   getCachedNews()          — last result without refetching
 *   getHeldInstruments()     — held tickers grouped by class
 *   _normalizeItems          — exported for unit tests
 */

import { S } from '../core/state.js';

const API_BASE = '/api';
const TTL_MS = 15 * 60 * 1000;
const US_BATCH = 5;        // market-intel getNews caps at 5 tickers server-side
const MAX_US_BATCHES = 2;  // 2 calls × 5 tickers — enough held-US coverage, no function changes

let _cache = { ts: 0, items: [], srcStatus: {} };

/** Crypto ticker → Yahoo RSS symbol */
const CRYPTO_YF = { BTC: 'BTC-USD', ETH: 'ETH-USD', SOL: 'SOL-USD', AVAX: 'AVAX-USD', DOT: 'DOT-USD' };

/**
 * Held tickers grouped by class, derived from holdings + open trades.
 * TR detection mirrors dashboard's classification (currency/broker heuristic).
 */
export function getHeldInstruments() {
  const out = { bist: [], us: [], crypto: [], funds: [] };
  const push = (arr, t) => { if (t && !arr.includes(t)) arr.push(t); };

  (S.holdings || []).forEach(h => {
    const tr = (h.currency === 'TRY') || /garanti|bist|matriks|midas/i.test(h.broker || '');
    if (h.type === 'Crypto') push(out.crypto, h.ticker);
    else if (h.type === 'Fund') push(out.funds, h.ticker);
    else if (h.type === 'Stock' && tr) push(out.bist, h.ticker);
    else if (h.type === 'Stock' || h.type === 'ETF') push(out.us, h.ticker);
    // Commodity: covered by market-intel macro topics, not per-ticker news
  });

  (S.trades || []).filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').forEach(t => {
    if (t.type === 'Crypto') push(out.crypto, t.ticker);
    else if ((t.currency || 'USD') === 'TRY') push(out.bist, t.ticker);
    else push(out.us, t.ticker);
  });

  return out;
}

/**
 * Normalize raw source payloads into the canonical lean item shape:
 * { id, ts, source, title, url, tickers[], scope }
 * scope ∈ bist | tr_macro | us | crypto
 * Exported for tests.
 */
export function _normalizeItems(gasItems, usItems, cryptoSet, macroItems = []) {
  const items = [];

  (gasItems || []).forEach(it => {
    if (!it || !it.title || !it.url) return;
    items.push({
      id: it.id || _hash(it.url),
      ts: typeof it.ts === 'number' ? it.ts : null,
      source: it.src || 'gas',
      title: it.title,
      url: it.url,
      tickers: Array.isArray(it.tickers) ? it.tickers : [],
      scope: it.scope === 'bist' ? 'bist' : 'tr_macro',
    });
  });

  (usItems || []).forEach(it => {
    if (!it || !it.title || !it.link) return;
    const ts = it.pubDate ? Date.parse(it.pubDate) : NaN;
    const tk = (it.ticker || '').replace(/-USD$/, '');
    items.push({
      id: _hash(it.link),
      ts: isNaN(ts) ? null : ts,
      source: 'yahoo',
      title: it.title,
      url: it.link,
      tickers: tk ? [tk] : [],
      scope: cryptoSet && cryptoSet.has(tk) ? 'crypto' : 'us',
    });
  });

  // Macro / market-wide items — no specific ticker; scope us_macro (affects the whole book)
  (macroItems || []).forEach(it => {
    if (!it || !it.title || !it.link) return;
    const ts = it.pubDate ? Date.parse(it.pubDate) : NaN;
    items.push({
      id: _hash(it.link),
      ts: isNaN(ts) ? null : ts,
      source: 'macro',
      title: it.title,
      url: it.link,
      tickers: [],
      scope: 'us_macro',
    });
  });

  // Dedupe on id, newest first (null ts sinks)
  const seen = new Set();
  return items
    .filter(it => (seen.has(it.id) ? false : seen.add(it.id)))
    .sort((a, b) => (b.ts || 0) - (a.ts || 0));
}

/**
 * Fetch held-instrument news from all sources. 15-min TTL unless force.
 * @param {Object} opts { force?: boolean }
 * @returns {Promise<{ts:number, items:Array, srcStatus:Object}>}
 */
export async function fetchAdvisoryNews(opts = {}) {
  if (!opts.force && _cache.ts && (Date.now() - _cache.ts) < TTL_MS) return _cache;

  const held = getHeldInstruments();
  const srcStatus = {};
  const tasks = [];

  // TR side — GAS proxy (same deployment as BIST prices, mode=news)
  const gasUrl = (S.settings && S.settings.bistProxyUrl) || '';
  if (gasUrl) {
    const qs = '?mode=news&limit=50' + (held.bist.length ? '&bist=' + encodeURIComponent(held.bist.join(',')) : '');
    tasks.push(
      fetch(gasUrl + qs, { signal: AbortSignal.timeout(25000) })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('GAS news HTTP ' + r.status))))
        .then(d => { srcStatus.gas = d.src_status || 'ok'; return { gas: d.items || [] }; })
        .catch(e => { srcStatus.gas = 'fail: ' + e.message; return { gas: [] }; })
    );
  } else {
    srcStatus.gas = 'skipped: no BIST proxy URL in Settings';
  }

  // US + crypto side — existing market-intel function, batched client-side
  // (server caps 5 tickers/call; we batch instead of touching the function)
  const usSymbols = [
    ...held.us,
    ...held.crypto.map(c => CRYPTO_YF[c] || (c + '-USD')),
  ];
  const batches = [];
  for (let i = 0; i < usSymbols.length && batches.length < MAX_US_BATCHES; i += US_BATCH) {
    batches.push(usSymbols.slice(i, i + US_BATCH));
  }
  batches.forEach((batch, i) => {
    tasks.push(
      fetch(`${API_BASE}/market-intel?source=news&tickers=${encodeURIComponent(batch.join(','))}`, { signal: AbortSignal.timeout(15000) })
        .then(r => (r.ok ? r.json() : Promise.reject(new Error('news HTTP ' + r.status))))
        .then(d => { srcStatus['us' + i] = 'ok'; return { us: Array.isArray(d) ? d : (d.news || []) }; })
        .catch(e => { srcStatus['us' + i] = 'fail: ' + e.message; return { us: [] }; })
    );
  });

  // Macro / market-wide news — one keyless call, not held-filtered (rates, inflation,
  // market + crypto sentiment, Turkey/lira). Feeds portfolio-level context, not per-ticker.
  tasks.push(
    fetch(`${API_BASE}/market-intel?source=macro`, { signal: AbortSignal.timeout(15000) })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('macro HTTP ' + r.status))))
      .then(d => { srcStatus.macro = 'ok'; return { macro: Array.isArray(d) ? d : [] }; })
      .catch(e => { srcStatus.macro = 'fail: ' + e.message; return { macro: [] }; })
  );

  const settled = await Promise.allSettled(tasks);
  let gasItems = [];
  let usItems = [];
  let macroItems = [];
  settled.forEach(res => {
    if (res.status !== 'fulfilled' || !res.value) return;
    if (res.value.gas) gasItems = gasItems.concat(res.value.gas);
    if (res.value.us) usItems = usItems.concat(res.value.us);
    if (res.value.macro) macroItems = macroItems.concat(res.value.macro);
  });

  const items = _normalizeItems(gasItems, usItems, new Set(held.crypto), macroItems);
  _cache = { ts: Date.now(), items, srcStatus };
  // console.log is stripped from production builds (vite pure) — warn survives, and the
  // window hook lets you inspect the live feed from DevTools: type __spektrxNews
  const failed = Object.values(srcStatus).some(v => typeof v === 'string' && v.indexOf('fail') === 0);
  if (failed) console.warn('[NEWS] Partial ingest —', JSON.stringify(srcStatus));
  console.log('[NEWS] Ingested', items.length, 'items —', JSON.stringify(srcStatus));
  try { window.__spektrxNews = _cache; } catch (_e) { /* non-browser env */ }
  return _cache;
}

/** Last fetched feed without refetching (may be empty). */
export function getCachedNews() {
  return _cache;
}

/** djb2 → hex (mirrors GAS hash_) */
function _hash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) & 0xffffffff;
  return (h >>> 0).toString(16);
}
